import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';
import { Download } from 'lucide-react';
import { useChartExport } from '@/hooks/useChartExport';
import { getROIColor, formatPercentage } from '@/lib/businesscaseCalculations';

/**
 * De gauge kent één getal: het rendement over het eerste jaar. Daarom oordeelt
 * hij niet meer.
 *
 * Tot 8 september 2026 stond hier "🎉 Uitstekende ROI - Zeer aantrekkelijke
 * investering" zodra `roiYear1 >= 20`, terwijl de risicokaart op dezelfde pagina
 * "Hoog Risico" toonde omdat de kasstroom over de looptijd negatief is (JWM
 * 30,26% bij -10.377 euro; zie docs/onderzoek/businesscase-tegenspraak.md). Een
 * component die de kasstroom niet in handen heeft, kan die tegenspraak niet zien
 * en hoort dus geen oordeel te vellen. Het oordeel staat nu op één plek, waar
 * beide getallen wél zijn: `beoordeelBusinesscase` in @/lib/businesscaseOordeel, gerenderd door
 * BusinesscaseSummary en RiskIndicator.
 *
 * Wat hier blijft staan is een meting mét periode: dit is jaar 1, en dat staat er
 * nu ook bij.
 */
interface ROIGaugeProps {
  /** `null` = niet te berekenen; dan toont de kaart een streepje, geen 0%. */
  roiYear1: number | null;
  roiBest: number | null;
  roiWorst: number | null;
  businesscaseName?: string;
}
export function ROIGauge({ roiYear1, roiBest, roiWorst, businesscaseName }: ROIGaugeProps) {
  const { exportToPNG } = useChartExport();
  const meetbaar = typeof roiYear1 === 'number' && Number.isFinite(roiYear1);
  const roiColor = getROIColor(roiYear1);

  // Een lege gauge bij een onbekend getal. Zonder deze afvang tekende
  // `null / 50 * 100` een boog op nul, wat er precies zo uitziet als een
  // gemeten 0% — het verschil dat deze kaart nu juist moet laten zien.
  const normalizedROI = meetbaar ? Math.min(Math.max(roiYear1 / 50 * 100, 0), 100) : 0;
  const gaugeData = [{
    name: 'ROI',
    value: normalizedROI,
    fill: roiColor
  }];
  return <Card className="border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>ROI jaar 1</CardTitle>
            <CardDescription>Rendement over uitsluitend het eerste jaar</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => exportToPNG('chart-roi', 'roi', businesscaseName)}
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center">
        <div className="relative w-full max-w-[300px] h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart cx="50%" cy="70%" innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={180} endAngle={0}>
              <RadialBar dataKey="value" cornerRadius={10} fill={roiColor} />
            </RadialBarChart>
          </ResponsiveContainer>
          
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pt-16">
            <p style={{
            color: roiColor
          }} className="font-bold text-2xl">
              {formatPercentage(roiYear1)}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {meetbaar ? 'ROI jaar 1' : 'Niet te berekenen'}
            </p>
          </div>
        </div>

        {/* Bandbreedte op jaar 1 — géén scenario over de looptijd. De oude
            labels "Best Case" en "Worst Case" suggereerden dat laatste, terwijl
            het niets anders is dan roiYear1 x 1,15 en x 0,90
            (businesscaseCalculations.ts:139-140). */}
        <div className="w-full mt-6 space-y-2">
          <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <span className="text-sm text-green-700 dark:text-green-300">Gunstig jaar 1 (+15%)</span>
            <span className="text-lg font-bold text-green-800 dark:text-green-200">
              {formatPercentage(roiBest)}
            </span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800">
            <span className="text-sm text-orange-700 dark:text-orange-300">Ongunstig jaar 1 (-10%)</span>
            <span className="text-lg font-bold text-orange-800 dark:text-orange-200">
              {formatPercentage(roiWorst)}
            </span>
          </div>
        </div>

        {/* Geen oordeel: deze kaart kent de kasstroom over de looptijd niet. */}
        <div className="w-full mt-4 p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground text-center">
            {meetbaar
              ? 'Deze drie percentages gaan alleen over het eerste jaar en zeggen niets over de hele looptijd. Het oordeel over de businesscase staat in de Financiële Samenvatting, waar de kasstroom en de terugverdientijd meetellen.'
              : 'Er is geen rendement te berekenen zolang de netto investering nul is of er geen jaarrijen zijn — een percentage van het niets bestaat niet. Vul de investering en de jaarrijen aan; het oordeel over de businesscase staat zolang op "nog geen oordeel".'}
          </p>
        </div>
      </CardContent>
    </Card>;
}
